import * as angular from 'angular';
import * as _ from 'lodash';
import { combineLatest, fromEvent as observableFromEvent, Observable } from 'rxjs';
import { filter, first, map, merge, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { NotebookSessionService } from '@mode/notebooks/domain';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { AppStore } from '~/types/main.types';
import { TimingService } from '@mode/shared/util-js';

import * as actions from '../../../actions/editor-actions';
import { CellActions } from '../../../actions/notebooks/cell.actions';
import { NotebookActions } from '../../../actions/notebooks/notebook.actions';
import { MixableBaseController } from '../../../common/component';
import { NotebookResource, SessionResource } from '@mode/legacy/data-access-model-js';
import { routerSelector } from '../../../reducers/selectors';
import { NormalizedNotebook, NotebookCellState, NotebookState } from '@mode/legacy/types-app-state';
import { NotebookSelectors } from '../../../selectors/notebook.selectors';
import { NotebookDataService } from '../../../services/notebook-data-service';
import { OrganizationResource } from '@mode/legacy/data-access-model-js';
import template from './notebook.html';
import { DataViewFacade } from '@mode/switch-views/contract';
import { DatasetStates, FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { NotebookFacade } from '@mode/notebooks/contract';
import { AiGatewayAuthFacade } from '@mode/shared/authorization/domain';

class NotebookController extends MixableBaseController {
  static $inject = [
    '$scope',
    'RxStoreService',
    '$q',
    'AnalyticsService',
    'NotebookDataService',
    'NotebookSessionService',
    'TimingService',
    'DataViewFacade',
    'FeatureFlagsFacade',
    'NotebookFacade',
    'AiGatewayAuthFacade',
  ];

  notebook: { _resource: NotebookResource; _new_notebook: boolean }; // mostly a notebook model
  report: any;
  visible: boolean;
  dummyCell: {
    content: string;
    content_type: string;
    folded: boolean;
  };
  newNotebookDataDeferred: any;

  canRun$: Observable<boolean>;
  canRestart$: Observable<boolean>;
  isRunning$: Observable<boolean>;
  isRegisterImportedDatasetEnabled: boolean;
  isRegisterImportedDatasetEnabled$: Observable<boolean>;
  selectedNotebookToken$: Observable<string>;
  notebook$: Observable<NormalizedNotebook>;
  notebookState$: Observable<NotebookState>;
  latestNotebookState$: Observable<NotebookState>;
  notebookStateIsAlert$: Observable<boolean>;
  notebookStateMessage$: Observable<string>;
  session$: Observable<SessionResource>;
  cellTokens$: Observable<string[]>;
  readonly sidebarOpen$: Observable<boolean>;
  accountContext$: Observable<OrganizationResource>;

  selectedCellToken: string;
  editingCellToken: string;
  refreshedTriggered = false;
  isPythonPerformanceFirstTier = false;
  toRefreshSwitchViewTokens: string[] = [];
  isCreateNotebookWithNoQueryEnabled$;
  isNotebookMigrtionEnableEnabled$: Observable<boolean>;
  constructor(
    private $scope,
    private rxStore: AppStore,
    private $q,
    private analyticsService: AnalyticsService,
    private notebookDataService: NotebookDataService,
    private notebookSessionService: NotebookSessionService,
    private timer: TimingService,
    private dataViewFacade: DataViewFacade,
    private featureFlagsFacade: FeatureFlagsFacade,
    private notebookFacade: NotebookFacade,
    private authFacade: AiGatewayAuthFacade
  ) {
    super();

    //set initial state
    this.dummyCell = {
      content: '',
      content_type: 'python',
      folded: false,
    };
    this.isRegisterImportedDatasetEnabled$ = this.featureFlagsFacade.asObservable(
      FeatureFlag.RegisterImportedDatasetsNotebooks
    );
    this.isCreateNotebookWithNoQueryEnabled$ = this.featureFlagsFacade.asObservable(
      FeatureFlag.CreateNotebookWithNoQuery
    );
    this.isNotebookMigrtionEnableEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.NotebookMigrationPhase1);
    this.sidebarOpen$ = this.rxStore.select((state) => state.transient.accountPreference.notebook_sidebar.open);
    this.authFacade.requestAuthorization();
  }

  $onInit() {
    // TBR after feature flag remove for reusable dataset Start
    this.isRegisterImportedDatasetEnabled$.subscribe((enabled) => {
      this.isRegisterImportedDatasetEnabled = enabled;
      if (enabled) {
        this.dataViewFacade.enhancedDatasetResults$.subscribe((enhancedDatasetResults) => {
          for (const enhancedDatasetResult of enhancedDatasetResults) {
            if (enhancedDatasetResult.state === DatasetStates.HasUpdateAvailable) {
              this.toRefreshSwitchViewTokens.push(enhancedDatasetResult.switchViewToken); // multiple datasets potentially to be refreshed
            }
          }
        });
      }
    });
    // TBR after feature flag remove for reusable dataset End

    this.accountContext$ = this.rxStore.select((state) => state.client.accountContext);
    this.accountContext$.subscribe(
      (value) => (this.isPythonPerformanceFirstTier = value.python_performance_first_tier)
    );
    this.selectedNotebookToken$ = this.rxStore.select(NotebookSelectors.selectedNotebookToken);
    this.notebook$ = this.rxStore.selectWithParam(
      NotebookSelectors.normalizedNotebookByToken,
      this.selectedNotebookToken$
    );

    this.cellTokens$ = this.rxStore
      .selectWithParam(NotebookSelectors.cellTokensByNotebook, this.selectedNotebookToken$)
      .pipe(map((tokens) => tokens.toArray()));

    this.notebookState$ = this.rxStore
      .selectWithParam(NotebookSelectors.statusBarStateByNotebook, this.selectedNotebookToken$)
      .pipe(
        tap((notebookState) => {
          if (notebookState === NotebookState.Ready && this.refreshedTriggered) {
            this.notebookDataService.runNotebook(this.notebook._resource.token);
            this.refreshedTriggered = !this.refreshedTriggered;
          }
        })
      );

    this.latestNotebookState$ = combineLatest(
      this.dataViewFacade.enhancedDatasetResults$,
      this.notebookState$,
      this.isRegisterImportedDatasetEnabled$
    ).pipe(
      map(([enhancedDatasetResults, notebookState, enabled]) => {
        if (enabled && enhancedDatasetResults.some((x) => x.state === DatasetStates.HasUpdateAvailable)) {
          return NotebookState.Alert;
        }
        return notebookState;
      })
    );

    this.notebookStateMessage$ = this.rxStore.selectWithParam(
      NotebookSelectors.stateMessageByNotebook,
      this.selectedNotebookToken$
    );

    this.notebook$
      .pipe(
        filter((notebook) => !!notebook && !!this.notebook),
        takeUntil(this.destroyed$)
      )
      .subscribe((notebook) => {
        this.notebook._resource = notebook.resource;
      });
    this.rxStore
      .select(NotebookSelectors.editingCellToken)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((token) => (this.editingCellToken = token));
    this.rxStore
      .select(NotebookSelectors.selectedCellToken)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((token) => (this.selectedCellToken = token));

    this.canRun$ = this.rxStore.selectWithParam(NotebookSelectors.canRunByNotebook, this.selectedNotebookToken$);
    this.canRestart$ = this.notebookState$.pipe(map((s) => s !== NotebookState.Session));
    this.session$ = this.rxStore.select(NotebookSelectors.selectedNotebookSession);

    this.notebookStateIsAlert$ = this.latestNotebookState$.pipe(
      map((notebookState: NotebookState) => notebookState === NotebookState.Alert)
    );

    observableFromEvent<MouseEvent>(document.body, 'mousedown')
      .pipe(
        merge(observableFromEvent<MouseEvent>(document.body, 'dblclick')),
        filter((event) => event.button === 0), // we only want left-clicks in this handler
        withLatestFrom(this.rxStore.select(routerSelector)),
        filter(([, router]) => router.name === 'application.reports.edit.notebook'), // Only fire mouse events when on notebook editor
        map(([event, router]) => event),
        withLatestFrom(
          this.rxStore.select(NotebookSelectors.selectedCellToken),
          this.rxStore.select(NotebookSelectors.selectedCellFolded),
          this.rxStore.select(NotebookSelectors.isEditing)
        ),
        takeUntil(this.destroyed$)
      )
      .subscribe(
        ([event, selectedCellToken, selectedCellFolded, isEditing]) => {
          const target = $(event.target);
          const viz = target.closest('.mode-notebook-cell:not(.cell-result)');
          const editor = target.closest('.mode-notebook-ace-editor');
          const cellToken = target.closest('.mode-notebook-cell-group').attr('id');

          // emulate blurring of ace editor, don't do this on double click or you won't be able to edit markdown cells
          if (event.type !== 'dblclick' && isEditing && (editor.length < 1 || cellToken !== selectedCellToken)) {
            this.$scope.$evalAsync(() => this.$scope.ctrl.endEditCell(selectedCellToken));
          }

          if (viz.length) {
            if (event.type === 'dblclick') {
              // enter editor
            } else {
              if (cellToken !== selectedCellToken) {
                // new selected cell, dispatch action to mark it. If we're in the editor area, dispatch the edit action instead
                if (editor.length) {
                  this.$scope.$evalAsync(() => this.$scope.ctrl.startEditCell(cellToken));
                } else {
                  this.$scope.$evalAsync(() =>
                    this.$scope.ctrl.selectCellAction(cellToken, !viz.hasClass('mode-notebook-cell'))
                  );
                }
              } else {
                // clicked inside of an already selected cell, dispatch edit if in editor area and not editing, otherwise end edit if currently editing
                if (editor.length > 0 && !isEditing) {
                  this.$scope.$evalAsync(() =>
                    this.$scope.ctrl.startEditCell(selectedCellToken, !viz.hasClass('mode-notebook-cell'))
                  );
                }
              }

              // if cell is folded and you left-click on it
              if (selectedCellFolded && cellToken === selectedCellToken) {
                this.$scope.$evalAsync(() => this.$scope.ctrl.unfoldCell(cellToken));
              }
            }
          } else {
            const group = target.closest('.mode-notebook-cell-group');
            if (group.length && selectedCellToken !== cellToken) {
              this.$scope.$evalAsync(() => this.$scope.ctrl.selectCellAction(cellToken, false));
            }
          }
        },
        (err) => console.error(err)
      );

    // Send Notebook Analytics events
    this.sendAnalyticEvents();
  }

  $onChanges(changes: angular.IOnChangesObject) {
    const canInitNotebook = changes.visible && changes.visible.currentValue === true;
    if (canInitNotebook) {
      this.initNotebook(true);
    }
  }

  initNotebook(show) {
    if (show && !this.notebookSessionService.initialized && this.notebook && this.notebook._new_notebook) {
      // setup a promise to resolve when data is loaded
      // so we can run the default cell immediately
      this.newNotebookDataDeferred = this.$q.defer();
      this.isCreateNotebookWithNoQueryEnabled$.subscribe((isCreateNotebookWithNoQueryEnabled) => {
        if (isCreateNotebookWithNoQueryEnabled) {
          this.notebookFacade.createNotebook(this.report.token);
        } else {
          this.rxStore.dispatch({
            type: NotebookActions.Type.Add,
            reportToken: this.report.token,
          });
        }
      });
    } else if (show && !this.notebookSessionService.initialized) {
      this.notebookDataService.initNotebook();
      this.notebookDataService.initSession(this.notebook._resource.token, true);
    }

    this.notebookSessionService.initialize();
  }

  toggleSidebar() {
    this.rxStore.dispatch({
      type: actions.EditorActionType.ToggleNotebookSidebar,
    });
  }

  loadReportRun() {
    // TBR after feature flag remove for reusable dataset
    this.refreshedTriggered = true;
    if (this.isRegisterImportedDatasetEnabled && this.toRefreshSwitchViewTokens.length) {
      this.refreshDatasetsNotebook();
    } else {
      this.notebookDataService.refreshSessionReportRun(this.notebook._resource.token);
    }
  }

  refreshDatasetsNotebook() {
    this.dataViewFacade.datasetRefreshAllClicked(this.report.token, this.toRefreshSwitchViewTokens);
    this.toRefreshSwitchViewTokens = [];
  }

  // Just for mirroring the state to see how it works
  selectCellAction(cellToken: string, resultPane: boolean) {
    if (this.selectedCellToken !== cellToken) {
      this.notebookDataService.selectCell(cellToken, resultPane);
    }
  }

  startEditCell(cellToken: string) {
    this.notebookDataService.beginEditCell(cellToken);
  }

  endEditCell(cellToken: string) {
    this.notebookDataService.endEditCell(cellToken);

    // this does happen to be redundant if the user blurred the editor by clicking the run button,
    // but no way around it at the moment
    this.notebookDataService.saveCell(cellToken);
  }

  unfoldCell = (token) => {
    if (token !== 'python-input') {
      this.rxStore.dispatch({
        type: CellActions.Type.FoldCell,
        cellToken: token,
      });
    } else {
      this.dummyCell.folded = false;
    }
  };

  toggleCellInLayout(cell: NotebookCellState) {
    if (cell.isInLayout) {
      this.notebookDataService.removeCellFromLayout(cell.token);
    } else {
      this.notebookDataService.addCellToLayout(cell.token);
    }
  }

  restartSession() {
    this.notebookDataService.initSession(this.notebook._resource.token, true);
  }

  /**
   * # Send Notebook Analytics events
   *
   * We send 2 events to segement for analysis:
   *
   * 1. *User Experience Started*
   *    The time it from the onEnter router event to when the notebook $onInit
   *    life cycle event fired.
   *    (router ./libs/shared/legacy/src/modules/editor-shell/updated.routes.ts)
   *
   * 2. *UserExperienceMilestone*
   *    An interaction event for when the "Ready" toast notification is shown
   */
  private sendAnalyticEvents() {
    const { startTime, endTime } = this.timer.end('loading_notebook');

    // Log an analytics event for the 'User Experience Started' event
    combineLatest([
      this.rxStore.select(NotebookSelectors.selectedNotebookToken),
      this.rxStore.select(NotebookSelectors.notebookAwaitingRunArray),
    ])
      .pipe(
        first(([notebookToken, runs]) => !!notebookToken && runs.count() > 0),
        map(([notebookToken, runs]) => ({ isNew: runs.get(notebookToken) }))
      )
      .subscribe(({ isNew }) => {
        this.analyticsService.track(AnalyticsEvent.Name.UserExperienceStarted, {
          context: AnalyticsEvent.Context.Notebook,
          context_id: this.report.token,
          is_new: isNew,
          location: AnalyticsEvent.SubContext.NotebookView,
          t_0: endTime - startTime,
          user_experience_id: this.analyticsService.userExperienceId,
        });
      });

    // Log an analytics event for the 'User Experience Milestone' when the
    // Ready toast notification is shown
    combineLatest([
      this.rxStore.select(NotebookSelectors.selectedNotebookToken),
      this.rxStore.selectWithParam(NotebookSelectors.statusBarStateByNotebook, this.selectedNotebookToken$),
    ])
      .pipe(
        first(([noteBookToken, state]) => noteBookToken && state === NotebookState.Ready),
        map(([noteBookToken]) => noteBookToken)
      )
      .subscribe((noteBookToken) => {
        this.analyticsService.track(AnalyticsEvent.Name.UserExperienceMilestone, {
          context: AnalyticsEvent.Context.Notebook,
          context_id: noteBookToken,
          interaction: 'interactable notebook',
          location: AnalyticsEvent.SubContext.NotebookView,
          user_experience_id: this.analyticsService.userExperienceId,
        });
      });
  }
}

export const modeNotebookComponent: angular.IComponentOptions = {
  template,
  controller: NotebookController,
  controllerAs: 'ctrl',
  bindings: {
    notebook: '<',
    report: '<',
    visible: '<',
  },
};
